/**
 * The MUI theme, carrying the J+P brand.
 *
 * This is where decision W4 lands: every `components/ui/*` primitive in the Next.js app —
 * Button, Card, Field, Badge, Modal, Section, Spinner — maps onto an MUI component, and the
 * bespoke kit is retired. What must **not** change is how the app looks. The Next.js app's
 * appearance is the specification here exactly as its API responses were for the backend, so
 * the overrides below exist to make MUI's defaults match what is already shipped, not to
 * introduce a new look.
 *
 * Three characteristics are worth naming, because they are the ones that make the app feel
 * like itself and are the easiest to lose to a component library's defaults:
 *
 * 1. **Cards are frosted glass over a video**, not flat panels. They are translucent with a
 *    backdrop blur, and they sit on a dark ground.
 * 2. **Nothing is elevated.** The Next.js app uses one card shadow and one panel shadow; MUI's
 *    25-step elevation scale would produce a materially different, busier surface hierarchy.
 * 3. **Corners are soft but not lozenges** — 14px for a card, 10px for a panel nested in one.
 *    The Next.js app's 24px is deliberately not carried over: combined with MUI's multiplying
 *    `borderRadius` shorthand it produced 48-72px inner radii, and every surface on the page
 *    read as a pill. See `tokens.ts` for the full account.
 */

import { createTheme, type ThemeOptions } from "@mui/material/styles";

import { CARD_SURFACE, FONT_STACK, RADIUS_INNER_PX, RADIUS_PX, SHADOWS, jp } from "./tokens";

const options: ThemeOptions = {
  palette: {
    mode: "light",
    primary: { main: jp.navy, dark: jp.navyDark, light: jp.accentLight, contrastText: "#FFFFFF" },
    // Slate is the muted-copy colour, not a second accent — so it is `secondary` only in the
    // sense that MUI needs one, and nothing should reach for it as a highlight.
    secondary: { main: jp.slate },
    /*
     * `main` stays the bright brand value — it is what MUI uses for fills, borders and
     * icons. `dark` is the AA-compliant text shade, and the component overrides below point
     * every *word* MUI colours semantically at it. Without that split, an outlined Alert or
     * a Chip label renders `main` as text at 2.3:1–3.7:1 on this app's card.
     */
    success: { main: jp.success, dark: jp.successText },
    warning: { main: jp.warning, dark: jp.warningText },
    error: { main: jp.danger, dark: jp.dangerText },
    background: { default: jp.ink, paper: jp.mist },
    text: { primary: jp.ink, secondary: jp.slate },
    divider: jp.line,
  },

  // The base every bare `borderRadius: N` in an `sx` prop multiplies. Deliberately the *inner*
  // radius, not the card's: when this was the 24px card radius, a `borderRadius: 3` written in
  // the ordinary MUI idiom rendered at 72px. Call sites now name an explicit token, and this
  // only governs any multiplier that slips back in — so it is set where that would be harmless.
  shape: { borderRadius: RADIUS_INNER_PX },

  typography: {
    fontFamily: FONT_STACK,
    // The display weight and tracking the `.font-display` utility applied.
    h1: { fontWeight: 700, letterSpacing: "-0.01em" },
    h2: { fontWeight: 700, letterSpacing: "-0.01em" },
    h3: { fontWeight: 700, letterSpacing: "-0.01em" },
    h4: { fontWeight: 700, letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, letterSpacing: "-0.01em" },
    h6: { fontWeight: 700, letterSpacing: "-0.01em" },
    // `.jp-label` / `.jp-field-label`: small, bold, upper-case, tracked out. Used for every
    // field label and section eyebrow, so it is worth being a typography variant rather than a
    // style repeated at every call site.
    overline: {
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      lineHeight: 1.6,
    },
    button: { textTransform: "none", fontWeight: 600 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: jp.ink,
          color: jp.ink,
          WebkitFontSmoothing: "antialiased",
        },
        "::selection": { backgroundColor: jp.accentLight, color: jp.ink },
      },
    },

    MuiPaper: {
      // Frosted glass, not elevation. `elevation={0}` everywhere means the 25-step shadow
      // scale never applies; a component that genuinely needs lift asks for one of the two
      // named shadows instead.
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          ...CARD_SURFACE,
          borderRadius: RADIUS_PX,
          boxShadow: SHADOWS.card,
          backgroundImage: "none",
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        /*
         * Outlined and text buttons take their *label* from `primary.dark`, not `primary.main`.
         *
         * This is the single widest contrast fix in the app: every unselected pill in every
         * `PillGroup` — lighting, weather, tier, variations, portrait framing, print size —
         * is an outlined primary button, and MUI colours those with `main`. At 13px on the
         * card that is 3.64:1, under the 4.5:1 AA needs. The *border* keeps `main`, so the
         * control still reads as the brand accent; only the glyphs darken.
         *
         * Contained buttons are unaffected: they put white on a filled navy ground, which is
         * already comfortable. Expressed as variant+colour classes rather than the
         * `outlinedPrimary` slot, which MUI v9 no longer emits — see the Chip and Alert
         * overrides below for the same trap.
         */
        root: {
          borderRadius: RADIUS_INNER_PX,
          paddingInline: 20,
          paddingBlock: 10,
          "&.MuiButton-outlined.MuiButton-colorPrimary": { color: jp.navyDark },
          "&.MuiButton-text.MuiButton-colorPrimary": { color: jp.navyDark },
        },
      },
    },

    /*
     * An errored field label is `error.main` by default — 2.93:1 at 16px, worse than the
     * body copy beside it, on the one label the user most needs to notice. The guardrail
     * puts the describe brief and the edit instruction into this state whenever text is
     * flagged, so it is a routine state here rather than an edge case.
     */
    MuiFormLabel: {
      styleOverrides: { root: { "&.Mui-error": { color: jp.dangerText } } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { "&.Mui-error": { color: jp.dangerText } } },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        // `.jp-input`: a solid white field with a hairline border, not the paper surface —
        // a translucent input over a video is unreadable while you type into it.
        root: {
          borderRadius: RADIUS_INNER_PX,
          backgroundColor: "#FFFFFF",
          "& fieldset": { borderColor: jp.line },
          "&:hover fieldset": { borderColor: jp.line },
          "&.Mui-focused fieldset": { borderColor: jp.navy, borderWidth: 2 },
        },
        input: { fontSize: "0.875rem" },
      },
    },

    /*
     * Semantic chips are tinted, not solid — which is both what the Next.js `Badge` did and
     * what keeps them legible. A filled MUI chip puts `contrastText` on `main`, and
     * `getContrastText` picks white above a 3:1 ratio; on `warning` that lands at 3.18:1,
     * which passes MUI's threshold and fails WCAG AA for a 12px bold label. A 12% tint with
     * the dark text shade and a 30% border reads as the same signal at 7:1.
     */
    MuiChip: {
      styleOverrides: {
        // Keyed on variant *and* colour as two separate classes
        // (`.MuiChip-filled.MuiChip-colorWarning`), not the combined `.MuiChip-filledWarning`
        // of earlier MUI majors — v9 splits them, and the combined selector silently matches
        // nothing, which is how the guardrail's category chips kept rendering white on amber.
        root: {
          borderRadius: RADIUS_INNER_PX,
          fontWeight: 600,
          "&.MuiChip-filled.MuiChip-colorSuccess": {
            backgroundColor: "rgba(34,197,94,0.12)",
            color: jp.successText,
            border: "1px solid rgba(34,197,94,0.3)",
          },
          "&.MuiChip-filled.MuiChip-colorWarning": {
            backgroundColor: "rgba(217,119,6,0.12)",
            color: jp.warningText,
            border: "1px solid rgba(217,119,6,0.3)",
          },
          "&.MuiChip-filled.MuiChip-colorError": {
            backgroundColor: "rgba(244,63,94,0.12)",
            color: jp.dangerText,
            border: "1px solid rgba(244,63,94,0.3)",
          },
        },
      },
    },

    /*
     * Same split, applied to alerts: the icon keeps the bright brand colour so the severity
     * still reads at a glance, while the message itself takes the dark shade. MUI derives
     * both from `main` by default, which puts the *text* at the failing ratio.
     */
    MuiAlert: {
      styleOverrides: {
        // Keyed on the colour class rather than per-variant slots: MUI v9 splits an alert's
        // classes into variant (`standard`/`outlined`/`filled`) and colour (`colorWarning`),
        // so one rule per colour covers every variant the app uses, and the bright icon is
        // restored underneath.
        root: {
          borderRadius: RADIUS_INNER_PX,
          "&.MuiAlert-colorSuccess": { color: jp.successText },
          "&.MuiAlert-colorWarning": { color: jp.warningText },
          "&.MuiAlert-colorError": { color: jp.dangerText },
          "&.MuiAlert-colorSuccess .MuiAlert-icon": { color: jp.success },
          "&.MuiAlert-colorWarning .MuiAlert-icon": { color: jp.warning },
          "&.MuiAlert-colorError .MuiAlert-icon": { color: jp.danger },
        },
        icon: { alignItems: "center" },
      },
    },

    MuiDialog: {
      styleOverrides: { paper: { borderRadius: RADIUS_PX, boxShadow: SHADOWS.panel } },
    },

    MuiLink: {
      defaultProps: { underline: "hover" },
      styleOverrides: { root: { color: jp.navy, fontWeight: 600 } },
    },
  },
};

export const theme = createTheme(options);
